import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { MagneticButton } from '@/components/MagneticButton'
import { RequestModal } from '@/components/RequestModal'
import { PlaceholderEvents } from '@/components/PlaceholderEvents'
import { placeholderEventsFor } from '@/data/placeholderEvents'
import { Icon } from '@/components/Icon'
import { PartnerCallout, SiteFooter, VideoPanel } from '@/components/ui/hero-sections'
import { MusicQuizModal } from '@/components/ui/MusicQuiz'
import { quizCategories } from '@/data/musicQuiz'
import { SiteNav } from '@/components/ui/site-chrome'
import { Chip, EventRow, Modal, SectionHeading, ParticipationNote } from '@/components/lc'
import type { CommunityMentor, CommunityPage, LifecrafterTierLane, LanePerson } from '@/data/content'
import { consultLanesByCommunity } from '@/data/content'
import type { CommunityHue } from '@/styles/lifecraft'
import { useSmoothScroll } from '@/lib/useSmoothScroll'
import { usePrefersReducedMotion } from '@/lib/useReducedMotion'
import { useAuth, communityWebappUrl, communityHomeUrl, consultWebappUrl, eventWebappUrl } from '@/lib/auth'
import { useCommunityEvents } from '@/lib/events'
import { useCommunityCircles } from '@/lib/circles'
import { useLifecrafterIndex, lookupLifecrafterId } from '@/lib/lifecrafters'
import { cn } from '@/lib/utils'

/* Consultation pricing from the client's master sheet (30-min one-time session).
   The higher 60-min rate is shown struck-through as the limited-time intro offer,
   matching Cosmos, so all communities read consistently. */
type TierPrice = Record<string, { was: string; now: string }>
const TIER_PRICE: TierPrice = {
  Premium: { was: '₹1,100', now: 'from ₹499' },
  Elite: { was: '₹1,500', now: 'from ₹1,199' },
}
/* Zen runs its own lifecrafter rates (per the client), lower than the shared
   default used by the other communities. */
const TIER_PRICE_ZEN: TierPrice = {
  Premium: { was: '₹1,000', now: 'from ₹499' },
  Elite: { was: '₹2,000', now: 'from ₹699' },
}

/* Communities whose mentors have priced programs in the webapp: every mentor
   gets a "Book consultation" CTA (→ 1-1 booking, like Cosmos) EXCEPT the names
   listed here, who keep the "Price on request" enquiry. Communities absent from
   this map keep every mentor on "Price on request". */
const MENTOR_BOOK_EXCEPT: Record<string, string[]> = {
  Zen: ['Ved Bhaskar'],
  Rhythm: ['Sunetra Chakraborty'],
}

/* Portrait card image for consult-lane people: the instructor's photo when we
   have one, else a neutral profile glyph on glass. */
function LanePhoto({ photo, pos, name }: { photo?: string; pos?: string; name: string }) {
  return photo ? (
    <img
      src={photo}
      alt={name}
      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 motion-reduce:group-hover:scale-100"
      style={{ objectPosition: pos }}
      loading="lazy"
    />
  ) : (
    <span className="grid h-full w-full place-items-center bg-lc-line text-lc-mute">
      <svg width="34" height="34" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-4.42 0-8 2.69-8 6v2h16v-2c0-3.31-3.58-6-8-6Z" /></svg>
    </span>
  )
}

/* Round avatar for consult-lane people: the instructor's photo when we have one,
   else a neutral profile glyph. */
function PersonAvatar({ photo, pos }: { photo?: string; pos?: string }) {
  return (
    <span className={cn('grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-full ring-2 ring-white/40', !photo && 'bg-lc-line text-lc-mute')} aria-hidden="true">
      {photo ? (
        <img src={photo} alt="" className="h-full w-full object-cover" style={{ objectPosition: pos }} loading="lazy" />
      ) : (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-4.42 0-8 2.69-8 6v2h16v-2c0-3.31-3.58-6-8-6Z" /></svg>
      )}
    </span>
  )
}

/* ================================================================== */
/*  Community base, LIGHT: the cream template for daylight circles     */
/*  (Spirituality, Rhythm for now). Same nav shape, same accents,      */
/*  same glass and type as the dark pages: one brand, two skies.       */
/* ================================================================== */

function Backdrop({ hue }: { hue: CommunityHue }) {
  const reduced = usePrefersReducedMotion()
  const blobs = [
    { cls: 'h-[46rem] w-[46rem] -left-48 -top-48 opacity-60', color: hue.blobs[0], anim: { x: [0, 70, 0], y: [0, 45, 0] }, d: 27 },
    { cls: 'h-[40rem] w-[40rem] -right-36 top-[14%] opacity-55', color: hue.blobs[1], anim: { x: [0, -60, 0], y: [0, 60, 0] }, d: 34 },
    { cls: 'h-[42rem] w-[42rem] left-[22%] top-[58%] opacity-50', color: hue.blobs[2], anim: { x: [0, 55, 0], y: [0, -55, 0] }, d: 40 },
    { cls: 'h-[24rem] w-[24rem] right-[16%] bottom-[6%] opacity-45', color: hue.blobs[3], anim: { x: [0, -45, 0], y: [0, -35, 0] }, d: 30 },
  ]
  return (
    <div className="grain pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
      <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${hue.bg[0]} 0%, ${hue.bg[1]} 55%, ${hue.bg[2]} 100%)` }} />
      {blobs.map((b, i) => (
        <motion.div
          key={i}
          className={cn('blob', b.cls)}
          style={{ background: b.color }}
          animate={reduced ? undefined : b.anim}
          transition={reduced ? undefined : { duration: b.d, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
    </div>
  )
}

/* ================================================================== */
/*  ProgramRail: a horizontal rail of bookable programs. Each card       */
/*  names the program, shows the LifeCrafters who run it, and offers a    */
/*  "book a slot" flow: pick a teacher, then continue to the (backend)    */
/*  booking step. A "view all" modal lists the full set.                  */
/* ================================================================== */
type ProgramData = NonNullable<CommunityPage['programs']>
type ProgramItem = ProgramData['items'][number]
type Crafters = ProgramItem['crafters']

const RAIL_EDGE =
  'calc(max((100vw - 1360px) / 2, 0px) + clamp(1.25rem, 0.5rem + 3vw, 3.5rem))'

function crafterLabel(cr: Crafters) {
  if (cr.length === 1) return `with ${cr[0].name}`
  if (cr.length === 2) return `${cr[0].name} & ${cr[1].name}`
  return `${cr[0].name}, ${cr[1].name} +${cr.length - 2}`
}

function CrafterAvatars({ crafters }: { crafters: Crafters }) {
  return (
    <div className="flex -space-x-2">
      {crafters.map((c, i) => (
        <img key={c.name + i} src={c.photo} alt={c.name} title={c.name} className="h-8 w-8 rounded-full object-cover ring-2 ring-white/70" />
      ))}
    </div>
  )
}

function ProgramCard({ item, onBook, className }: { item: ProgramItem; onBook: (i: ProgramItem) => void; className?: string }) {
  return (
    <div className={cn('lc-glass flex flex-col rounded-[22px] p-5 !shadow-none', className)}>
      <h3 className="font-display text-xl leading-tight">{item.name}</h3>
      <p className="mt-2 flex-1 text-[14px] leading-relaxed text-lc-mute">{item.blurb}</p>
      <div className="mt-4 flex items-center gap-2.5">
        <CrafterAvatars crafters={item.crafters} />
        <span className="min-w-0 truncate text-[12px] text-lc-mute">{crafterLabel(item.crafters)}</span>
      </div>
      <button
        onClick={() => onBook(item)}
        className="mt-5 rounded-pill bg-lime px-5 py-2.5 text-[14px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100"
      >
        Book a slot
      </button>
    </div>
  )
}

function ProgramRail({ data, accent }: { data: ProgramData; accent: string }) {
  const navigate = useNavigate()
  const { authed } = useAuth()
  const [viewAll, setViewAll] = useState(false)
  const [booking, setBooking] = useState<ProgramItem | null>(null)
  const [selected, setSelected] = useState<number | null>(null)
  const [step, setStep] = useState<'pick' | 'done'>('pick')

  const RAIL_COUNT = 6
  const rail = data.items.slice(0, RAIL_COUNT)
  const hasMore = data.items.length > RAIL_COUNT

  const openBooking = (item: ProgramItem) => {
    if (!authed) { navigate('/login'); return } // gate booking behind login
    setViewAll(false)
    setSelected(item.crafters.length === 1 ? 0 : null)
    setStep('pick')
    setBooking(item)
  }
  const closeBooking = () => { setBooking(null); setStep('pick'); setSelected(null) }

  return (
    <section id="programs" className="pb-20">
      <div className="container-lc">
        {/* gutter on the inner row, not on container-lc itself: container-lc
            zeroes its own padding on mobile and would override a px-* here */}
        <div className="flex flex-wrap items-end justify-between gap-4 px-5 md:px-0">
          <SectionHeading kicker={data.kicker} title={data.title} intro={data.intro} />
          {hasMore && (
            <button
              onClick={() => setViewAll(true)}
              className="label lc-glass shrink-0 rounded-pill px-5 py-2.5 !text-[11px] text-lc-ink transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100"
            >
              View all {data.items.length}
            </button>
          )}
        </div>
      </div>

      <div
        className="no-scrollbar mt-8 flex snap-x gap-4 overflow-x-auto pb-3"
        style={{ paddingLeft: RAIL_EDGE, paddingRight: RAIL_EDGE, scrollPaddingLeft: RAIL_EDGE }}
      >
        {rail.map((item) => (
          <ProgramCard key={item.name} item={item} onBook={openBooking} className="w-[280px] shrink-0 snap-start" />
        ))}
        {hasMore && (
          <button
            onClick={() => setViewAll(true)}
            className="lc-glass-soft grid w-[190px] shrink-0 snap-start place-items-center gap-2 rounded-[22px] p-5 text-center transition-transform duration-300 ease-brand hover:-translate-y-1"
          >
            <span className="font-display text-lg leading-tight">View all<br />{data.items.length} programs</span>
            <span className="label !text-[11px]" style={{ color: accent }}>Open →</span>
          </button>
        )}
      </div>

      {/* view-all: the full list, each program as its own card */}
      <Modal open={viewAll} onClose={() => setViewAll(false)} title={data.title} size="xl" theme="light">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.items.map((item) => (
            <ProgramCard key={item.name} item={item} onBook={openBooking} />
          ))}
        </div>
      </Modal>

      {/* booking: pick a teacher, then a backend-handled step */}
      <Modal open={!!booking} onClose={closeBooking} theme="light" title={booking ? (step === 'pick' ? 'Book a slot' : 'Nearly there') : ''}>
        {booking && step === 'pick' && (
          <div>
            <p className="text-[14px] text-lc-mute">
              <span className="font-semibold text-lc-ink">{booking.name}</span>: choose the teacher you'd like to book with.
            </p>
            <div className="mt-4 space-y-2.5">
              {booking.crafters.map((c, i) => {
                const active = selected === i
                return (
                  <button
                    key={c.name + i}
                    onClick={() => setSelected(i)}
                    aria-pressed={active}
                    className={cn('flex w-full items-center gap-3 rounded-[16px] p-3 text-left transition-all', !active && 'lc-glass-soft')}
                    style={active ? { boxShadow: `0 0 0 2px ${accent}`, background: 'var(--lc-glass-flat)' } : undefined}
                  >
                    <img src={c.photo} alt="" className="h-11 w-11 shrink-0 rounded-full object-cover ring-2 ring-white/50" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] font-semibold">{c.name}</p>
                      <p className="truncate text-[12px] text-lc-mute">{c.craft}</p>
                    </div>
                    <span
                      className={cn('grid h-6 w-6 shrink-0 place-items-center rounded-full border text-white', !active && 'border-lc-line')}
                      style={active ? { background: accent, borderColor: accent } : undefined}
                    >
                      {active && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m4.5 12.5 5 5 10-11" /></svg>}
                    </span>
                  </button>
                )
              })}
            </div>
            <button
              disabled={selected === null}
              onClick={() => setStep('done')}
              className={cn(
                'mt-6 w-full rounded-pill px-6 py-3 text-[15px] font-bold transition-transform duration-300 ease-brand',
                selected === null ? 'cursor-not-allowed bg-lc-line text-lc-mute' : 'bg-lime text-night hover:scale-[1.02] motion-reduce:hover:scale-100',
              )}
            >
              Continue
            </button>
          </div>
        )}
        {booking && step === 'done' && selected !== null && (
          <div className="text-center">
            <span className="mx-auto grid h-14 w-14 place-items-center rounded-full text-white" style={{ background: accent }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m4.5 12.5 5 5 10-11" /></svg>
            </span>
            <p className="mt-4 text-[15px] text-lc-ink">
              <span className="font-semibold">{booking.name}</span> with <span className="font-semibold">{booking.crafters[selected].name}</span>.
            </p>
            <p className="mt-2 text-[14px] leading-relaxed text-lc-mute">
              Choosing a time and payment is the next step, handled once the booking backend is connected.
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Chip tone="glass" small>Booking · coming soon</Chip>
              <button onClick={closeBooking} className="rounded-pill bg-lime px-5 py-2.5 text-[14px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100">
                Done
              </button>
            </div>
          </div>
        )}
      </Modal>
    </section>
  )
}

export default function CommunityBase({ page }: { page: CommunityPage }) {
  useSmoothScroll()
  const navigate = useNavigate()
  const { authed } = useAuth()
  const [openType, setOpenType] = useState(page.types?.items[0]?.name ?? null)
  const [openDaily, setOpenDaily] = useState<string | null>(null)
  const [quizOpen, setQuizOpen] = useState(false)
  const [profileMentor, setProfileMentor] = useState<CommunityMentor | null>(null)
  // Mentors are priced per person → "Price on request" opens an enquiry form.
  const [requestFor, setRequestFor] = useState<string | null>(null)
  // Horizontal mentor rail: arrows at each end make it obvious the row scrolls
  // (and that mentors continue off-screen). We fade an arrow out at its extreme.
  const mentorRailRef = useRef<HTMLDivElement>(null)
  const [railEnds, setRailEnds] = useState({ atStart: true, atEnd: false })
  const updateRailEnds = () => {
    const el = mentorRailRef.current
    if (!el) return
    const overflows = el.scrollWidth - el.clientWidth > 4
    setRailEnds({
      atStart: el.scrollLeft <= 4,
      atEnd: !overflows || el.scrollLeft + el.clientWidth >= el.scrollWidth - 4,
    })
  }
  const scrollRail = (dir: -1 | 1) => {
    const el = mentorRailRef.current
    if (el) el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.85, 640), behavior: 'smooth' })
  }
  useEffect(() => {
    updateRailEnds()
    window.addEventListener('resize', updateRailEnds)
    return () => window.removeEventListener('resize', updateRailEnds)
  }, [])
  // Same treatment for the schools / genres scroller.
  const typesRailRef = useRef<HTMLDivElement>(null)
  const [typesEnds, setTypesEnds] = useState({ atStart: true, atEnd: false })
  const updateTypesEnds = () => {
    const el = typesRailRef.current
    if (!el) return
    const overflows = el.scrollWidth - el.clientWidth > 4
    setTypesEnds({
      atStart: el.scrollLeft <= 4,
      atEnd: !overflows || el.scrollLeft + el.clientWidth >= el.scrollWidth - 4,
    })
  }
  const scrollTypesRail = (dir: -1 | 1) => {
    const el = typesRailRef.current
    if (el) el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.85, 640), behavior: 'smooth' })
  }
  useEffect(() => {
    updateTypesEnds()
    window.addEventListener('resize', updateTypesEnds)
    return () => window.removeEventListener('resize', updateTypesEnds)
  }, [])
  // This month's real events for the live-wired communities (cosmos/rhythm/zen);
  // everyone else — and any month with none scheduled — keeps the static list.
  const liveEvents = useCommunityEvents(page.name)
  // Live circles from the webapp for this community. Additive + API-driven: the
  // section only renders when the feed returns circles (empty/unreachable → hidden).
  const liveCircles = useCommunityCircles(page.name)
  // Rhythm/Zen show tier-grouped consultation lanes (Premium/Elite) instead of
  // the static sub-category lanes. Zen carries the real yoga-instructor roster.
  const tierLanes = consultLanesByCommunity[page.slug]
  const useTierLanes = !!tierLanes
  // "View all" popup for a whole tier, and a single-person bio popup.
  const [laneModal, setLaneModal] = useState<LifecrafterTierLane | null>(null)
  const [laneBio, setLaneBio] = useState<LanePerson | null>(null)
  // Logged out → any "book" action routes to login first (keeps the in-page
  // scroll-to-events behaviour once signed in).
  const guardBook = (e: React.MouseEvent) => { if (!authed) { e.preventDefault(); navigate('/login') } }
  // Logged out → "join" actions funnel to signup instead.
  const guardJoin = (e: React.MouseEvent) => { if (!authed) { e.preventDefault(); navigate('/signup') } }
  // Logged-in members deep-link straight into the webapp community tabs; the
  // guards above still gate the logged-out case. Falls back to the in-page
  // anchor / list when this community isn't on the webapp yet.
  const tierPrice = page.name === 'Zen' ? TIER_PRICE_ZEN : TIER_PRICE
  const consultTarget = consultWebappUrl(page.name) ?? '#consult'
  // Per-crafter booking: match a curated crafter to their webapp booking id;
  // unmatched crafters fall back to the generic community booking (consultTarget).
  const lcIndex = useLifecrafterIndex()
  const crafterBook = (name: string) => consultWebappUrl(page.name, lookupLifecrafterId(lcIndex, name)) ?? '#consult'
  // Join → the community's webapp home (the webapp handles guest vs member).
  const joinTarget = communityHomeUrl(page.name) ?? '#join'
  const eventsTarget = communityWebappUrl(page.name, 'events') ?? '/communities'
  const circlesTarget = communityWebappUrl(page.name, 'community_circles') ?? '#circles'
  return (
    <div className="lc-light relative min-h-screen overflow-x-clip text-lc-ink" style={{ background: page.hue.bg[0] }}>
      <Backdrop hue={page.hue} />
      <SiteNav />

      <main className="relative z-10">
        {/* hero: copy left, intro video right */}
        <section id="top" className="relative px-5 pb-16 pt-36 md:pt-44">
          <div className="container-lc grid items-start gap-12 lg:grid-cols-12">
            <div className="order-1 lg:col-span-6">
              <p className="label text-lc-mute"><span style={{ color: page.hue.deep }}>{page.name}</span> · {page.topic}</p>
              <h1 className="mt-4 font-display text-[clamp(2.6rem,1.4rem+5vw,7rem)] leading-[0.95]">
                {page.name}
              </h1>
              <p className="mt-2 font-display text-[clamp(1.35rem,0.9rem+2.2vw,3.2rem)] leading-[1.05]">
                {page.titleA} <span className="rounded-lg bg-lime px-2.5 text-night">{page.titleWord}</span>.
              </p>
              <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-lc-mute sm:mt-6 xl:text-[17px]">{page.intro}</p>
              <div className="mt-7 flex flex-wrap items-center gap-3 sm:mt-8 sm:gap-4">
                <MagneticButton href={consultTarget} className="rounded-pill bg-lime px-6 py-3 text-[15px] font-bold text-night xl:px-7 xl:py-3.5 xl:text-[16px]">
                  Book a consultation
                </MagneticButton>
                <MagneticButton href={joinTarget} className="lc-glass rounded-pill px-6 py-3 text-[15px] font-bold text-lc-ink xl:px-7 xl:py-3.5 xl:text-[16px]">
                  Join the community
                </MagneticButton>
                <a href="#events" className="label group inline-flex items-center gap-2 text-lc-mute transition-colors hover:text-lc-ink">
                  Join an event <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                </a>
              </div>
              <ParticipationNote className="mt-5" />
              <p className="mt-4 max-w-md text-[14px] leading-relaxed text-lc-mute">
                To join an event you can also WhatsApp us on{' '}
                <a href="https://wa.me/918796790333" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-lc-ink underline-offset-2 hover:underline">
                  <Icon name="whatsapp" size={14} /> +91 8796790333
                </a>.
              </p>
            </div>

            <div className="order-2 lg:col-span-6">
              <VideoPanel
                layout={page.videoLayout ?? 'single'}
                videos={page.videos ?? [{ src: '/video/community-demo.mp4', poster: page.image }]}
              />
            </div>
          </div>
        </section>

        {/* highlights */}
        {page.highlights && (
          <section className="px-5 pb-20">
            <div className="container-lc">
              <SectionHeading kicker={`Inside ${page.name}`} title="What your week looks like." center />
              <div className="mt-10 grid gap-4 sm:grid-cols-3">
                {page.highlights.map((h) => (
                  <div key={h.title} className="lc-glass group rounded-card p-6 transition-transform duration-300 ease-brand hover:-translate-y-1">
                    <span className="lc-glass-soft grid h-11 w-11 place-items-center rounded-tile transition-transform duration-500 group-hover:rotate-12" style={{ color: page.hue.deep }}>
                      <Icon name={h.icon} size={20} />
                    </span>
                    <h3 className="mt-5 font-display text-xl">{h.title}</h3>
                    <p className="mt-2 text-[14px] leading-relaxed text-lc-mute">{h.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* the daily loop: a day-ribbon of small practices */}
        {page.daily && (
          <section className="px-5 pb-20">
            <div className="container-lc">
              <SectionHeading kicker={page.daily.kicker} title={page.daily.title} intro={page.daily.intro} center />
              <div className="relative mt-14">
                {/* the thread through the day */}
                <div className="absolute left-0 right-0 top-6 hidden border-t-2 border-dashed lg:block" style={{ borderColor: `${page.hue.deep}44` }} aria-hidden="true" />
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
                  {page.daily.items.map((d) => (
                    <button key={d.title} onClick={() => setOpenDaily(d.title)} className="group relative text-left">
                      <span
                        className="relative z-10 grid h-12 w-12 place-items-center rounded-full text-white shadow-lg transition-transform duration-300 ease-brand group-hover:scale-110 motion-reduce:group-hover:scale-100"
                        style={{ background: page.hue.deep }}
                      >
                        <Icon name={d.icon} size={19} />
                      </span>
                      <p className="label mt-4 !text-[11px]" style={{ color: page.hue.deep }}>{d.when}</p>
                      <h3 className="mt-1.5 font-display text-xl">{d.title}</h3>
                      <p className="mt-1 text-[13px] leading-snug text-lc-mute">{d.teaser}</p>
                      <p className="label mt-2.5 !text-[11px] text-lc-mute opacity-0 transition-opacity duration-300 group-hover:opacity-100">open →</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {(() => {
              const d = page.daily.items.find((it) => it.title === openDaily)
              return (
                <Modal open={!!d} onClose={() => setOpenDaily(null)} title={d?.title ?? ''} theme="light">
                  <div className="space-y-3">
                    {d?.body.map((line) => (
                      <p key={line} className="text-[15px] leading-relaxed text-lc-mute">{line}</p>
                    ))}
                  </div>
                  <div className="mt-6 flex items-center justify-between gap-4">
                    <Chip tone="glass" small>Demo content</Chip>
                    <a
                      href="#join"
                      onClick={() => setOpenDaily(null)}
                      className="rounded-pill bg-lime px-5 py-2.5 text-[14px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02]"
                    >
                      Get the daily loop in {page.name}
                    </a>
                  </div>
                </Modal>
              )
            })()}
          </section>
        )}

        {/* Music Explorer Challenge: the Rhythm daily quiz */}
        {page.quiz && (
          <section className="px-5 pb-20">
            <div className="container-lc">
              <SectionHeading kicker={page.quiz.kicker} title={page.quiz.title} intro={page.quiz.intro} center />
              <div className="lc-glass mx-auto mt-10 max-w-3xl overflow-hidden rounded-card">
                <div className="grid items-center gap-6 p-5 xl:p-7 sm:grid-cols-[1fr_auto] sm:p-8">
                  <div>
                    <div className="flex flex-wrap gap-2">
                      {quizCategories.map((c) => (
                        <span key={c.key} className="lc-glass-soft inline-flex items-center gap-1.5 rounded-pill px-3 py-1 text-[12px] font-semibold text-lc-ink">
                          <Icon name={c.icon} size={13} /> {c.label}
                        </span>
                      ))}
                    </div>
                    <p className="mt-4 text-[15px] leading-relaxed text-lc-mute">
                      20 questions · 4 categories · earn coins, build streaks and climb from
                      Beginner to Music Maestro. Every player gets a freshly shuffled set.
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-lc-mute">
                      <span><b className="text-lc-ink">+10</b> per correct</span>
                      <span><b className="text-lc-ink">+25</b> a 5-answer streak</span>
                      <span><b className="text-lc-ink">+100</b> a perfect 20/20</span>
                      <span><b className="text-lc-ink">+20</b> for finishing</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setQuizOpen(true)}
                    className="rounded-pill bg-lime px-6 py-3 text-[15px] font-bold text-night shadow-lg transition-transform duration-300 ease-brand hover:scale-[1.03]"
                  >
                    {page.quiz.cta} →
                  </button>
                </div>
              </div>
            </div>
            <MusicQuizModal open={quizOpen} onClose={() => setQuizOpen(false)} hue={page.hue} />
          </section>
        )}

        {/* bookable programs rail, when a community defines them */}
        {page.programs && <ProgramRail data={page.programs} accent={page.hue.deep} />}

        {/* life stage journeys */}
        {page.journeys && (
          <section className="px-5 pb-20">
            <div className="container-lc">
              <SectionHeading kicker={page.journeys.kicker} title={page.journeys.title} intro={page.journeys.intro} center />
              <div className="mt-10 grid gap-4 md:grid-cols-3">
                {page.journeys.stories.map((st) => (
                  <figure key={st.age} className="lc-glass group overflow-hidden rounded-card transition-transform duration-300 ease-brand hover:-translate-y-1">
                    <div className="relative h-44 overflow-hidden">
                      <img src={st.photo} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 motion-reduce:group-hover:scale-100" />
                      <span className="label absolute left-3 top-3 rounded-pill bg-night/60 px-2.5 py-1 !text-[11px] text-cream backdrop-blur">{st.age}</span>
                    </div>
                    <figcaption className="p-5">
                      <p className="font-display text-xl leading-tight">{st.line}</p>
                      <p className="mt-2 text-[13px] leading-relaxed text-lc-mute">{st.who}</p>
                    </figcaption>
                  </figure>
                ))}
              </div>
              <div className="lc-glass-soft mt-6 rounded-card p-6">
                <p className="label text-lc-mute">Begin your journey at your stage of life</p>
                <div className="mt-5 grid gap-6 md:grid-cols-3">
                  {page.journeys.stages.map((stg) => (
                    <div key={stg.name} className="flex flex-col">
                      <p className="font-display text-xl">{stg.name} <span className="label ml-1 !text-[11px] text-lc-mute">{stg.range}</span></p>
                      <p className="mt-1.5 text-[14px] leading-relaxed text-lc-mute">{stg.line}</p>
                      <a href="#join" className="label group mt-3 inline-flex items-center gap-1.5 !text-[11px]" style={{ color: page.hue.deep }}>
                        {stg.cta} <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* LifeCrafters lead: big consultation lanes, then compact mentors */}
        {page.people && (
          <section id="consult" className="px-5 pb-20">
            <div className="container-lc">
              <SectionHeading kicker="Your LifeCrafters" title="Book a consultation." center />
              <p className="mx-auto mt-4 max-w-xl text-center text-lg text-lc-mute">
                Verified LifeCrafters, ready when you are. Tap a name for their bio, or book straight away.
              </p>

              {/* LifeCrafters: quick consultations, three lanes */}
              <div className="mt-10">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <p className="label text-lc-mute">LifeCrafters · book a consultation</p>
                  <span className="lc-glass-soft rounded-pill px-3 py-1 label !text-[11px]" style={{ color: page.hue.deep }}>
                    Limited-time rates · until 30 Sept
                  </span>
                </div>
                <div className={cn('mt-5 grid gap-4', useTierLanes ? 'md:grid-cols-2' : 'md:grid-cols-3')}>
                  {useTierLanes
                    ? tierLanes.map((lane) => (
                        <div key={lane.tier} className="lc-glass-soft rounded-[22px] p-6">
                          <div className="flex items-baseline justify-between gap-3">
                            <p className="font-display text-2xl">{lane.tier}</p>
                            <span className="label shrink-0 !text-[11px]" style={{ color: page.hue.deep }}>
                              {tierPrice[lane.tier] ? (
                                <><span className="text-lc-mute line-through decoration-1">{tierPrice[lane.tier].was}</span> {tierPrice[lane.tier].now} · until 30 Sept</>
                              ) : (
                                <><span className="text-lc-mute line-through decoration-1">{lane.note}</span> · Free</>
                              )}
                            </span>
                          </div>
                          <div className="mt-5 space-y-4">
                            {lane.people.slice(0, 3).map((p) => (
                              <div key={p.name} className="flex items-center gap-4">
                                {p.bio ? (
                                  <button
                                    type="button"
                                    onClick={() => setLaneBio(p)}
                                    className="group relative block h-16 w-16 shrink-0 overflow-hidden rounded-full ring-2 ring-white/40"
                                    aria-label={`About ${p.name}`}
                                  >
                                    <LanePhoto photo={p.photo} pos={p.imgPos} name={p.name} />
                                  </button>
                                ) : (
                                  <span className="group relative block h-16 w-16 shrink-0 overflow-hidden rounded-full ring-2 ring-white/40">
                                    <LanePhoto photo={p.photo} pos={p.imgPos} name={p.name} />
                                  </span>
                                )}
                                <div className="min-w-0 flex-1">
                                  {p.bio ? (
                                    <button type="button" onClick={() => setLaneBio(p)} className="block max-w-full truncate text-left text-[15px] font-semibold underline decoration-transparent underline-offset-2 transition-colors hover:decoration-current">
                                      {p.name}
                                    </button>
                                  ) : (
                                    <p className="truncate text-[15px] font-semibold">{p.name}</p>
                                  )}
                                  <p className="mt-0.5 truncate text-[12px] text-lc-mute">{p.focus ?? page.topic}</p>
                                </div>
                                <a href={crafterBook(p.name)} className="bg-lime shrink-0 rounded-pill px-4 py-2 text-[13px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100">
                                  Book
                                </a>
                              </div>
                            ))}
                          </div>
                          {lane.people.length > 3 && (
                            <button type="button" onClick={() => setLaneModal(lane)} className="lc-glass mt-4 w-full rounded-pill px-4 py-2 text-[12px] font-bold text-lc-ink transition-transform duration-300 ease-brand hover:scale-[1.01] motion-reduce:hover:scale-100">
                              View all {lane.people.length}
                            </button>
                          )}
                        </div>
                      ))
                    : page.people.categories.map((cat) => (
                        <div key={cat.category} className="lc-glass-soft rounded-[22px] p-6">
                          <div className="flex items-baseline justify-between gap-3">
                            <p className="font-display text-2xl">{cat.category}</p>
                            <span className="label shrink-0 !text-[11px]" style={{ color: page.hue.deep }}>
                              <span className="text-lc-mute line-through decoration-1">from {cat.startsAt}</span> · Free
                            </span>
                          </div>
                          <div className="mt-5 space-y-4">
                            {cat.people.map((p) => (
                              <div key={p.name} className="flex items-center gap-4">
                                <span className="group relative block h-16 w-16 shrink-0 overflow-hidden rounded-full ring-2 ring-white/40">
                                  <LanePhoto photo={p.photo} name={p.name} />
                                </span>
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-[15px] font-semibold">{p.name}</p>
                                  <p className="mt-0.5 truncate text-[12px] text-lc-mute">{p.line}</p>
                                </div>
                                <a href={crafterBook(p.name)} className="bg-lime shrink-0 rounded-pill px-4 py-2 text-[13px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100">
                                  Book
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                </div>
              </div>

              {/* mentors: deeper guidance — compact cards below the LifeCrafters */}
              <div className="mt-14">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <p className="label text-lc-mute">Mentors · guides for every path</p>
                  <span className="label !text-[11px]" style={{ color: page.hue.deep }}>Personal guidance · price on request</span>
                </div>
                {(() => {
                  {/* few mentors → a full-width grid of bigger cards with their blurbs;
                      many → a compact horizontal rail with scroll arrows at each end */}
                  const few = page.people.mentors.length <= 4
                  const cards = page.people.mentors.map((m) => (
                    <figure key={m.name} className={cn('lc-glass group flex flex-col overflow-hidden rounded-[22px] !shadow-none transition-transform duration-300 hover:-translate-y-1', !few && 'w-[280px] shrink-0 snap-start')}>
                      <div className="relative aspect-square overflow-hidden">
                        <img src={m.photo} alt={m.name} style={{ objectPosition: m.focus, scale: m.zoom ? String(m.zoom) : undefined }} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 motion-reduce:group-hover:scale-100" />
                      </div>
                      <figcaption className="flex flex-1 flex-col p-5">
                        <p className="font-display text-xl">{m.name}</p>
                        <p className="label mt-0.5 !text-[11px] text-lc-mute">{m.craft}</p>
                        {m.line && <p className="mt-2.5 line-clamp-2 text-[14px] leading-relaxed text-lc-mute">{m.line}</p>}
                        <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-4">
                          {(MENTOR_BOOK_EXCEPT[page.name] && !MENTOR_BOOK_EXCEPT[page.name].includes(m.name)) ? (
                            <a href={crafterBook(m.name)} className="rounded-pill bg-lime px-3 py-1.5 text-[12px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100">
                              Book consultation
                            </a>
                          ) : (
                            <button onClick={() => setRequestFor(m.name)} className="rounded-pill bg-lime px-3 py-1.5 text-[12px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100">
                              Price on request
                            </button>
                          )}
                          {m.about && (
                            <button onClick={() => setProfileMentor(m)} className="label !text-[11px] underline-offset-2 hover:underline" style={{ color: page.hue.deep }}>
                              View profile
                            </button>
                          )}
                        </div>
                      </figcaption>
                    </figure>
                  ))
                  if (few) {
                    return (
                      <div className={cn('mt-5 grid gap-4 sm:grid-cols-2', page.people.mentors.length >= 4 ? 'xl:grid-cols-4' : 'xl:grid-cols-3')}>
                        {cards}
                      </div>
                    )
                  }
                  // Arrows only on sm+ ; on mobile the swipe/scroll is already obvious.
                  const arrowCls = 'lc-glass absolute top-[140px] z-10 hidden h-11 w-11 -translate-y-1/2 place-items-center rounded-full text-lc-ink transition duration-300 sm:grid'
                  return (
                  <div className="relative left-1/2 mt-5 w-screen -translate-x-1/2">
                    <div
                      ref={mentorRailRef}
                      onScroll={updateRailEnds}
                      className="no-scrollbar flex snap-x gap-4 overflow-x-auto pb-2"
                      style={{ paddingLeft: RAIL_EDGE, paddingRight: RAIL_EDGE, scrollPaddingLeft: RAIL_EDGE, scrollPaddingRight: RAIL_EDGE }}
                    >
                      {cards}
                    </div>
                    <button
                      type="button"
                      aria-label="Show previous mentors"
                      onClick={() => scrollRail(-1)}
                      disabled={railEnds.atStart}
                      className={cn(arrowCls, 'left-3 sm:left-5', railEnds.atStart ? 'pointer-events-none opacity-0' : 'opacity-100 hover:scale-105')}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
                    </button>
                    <button
                      type="button"
                      aria-label="Show more mentors"
                      onClick={() => scrollRail(1)}
                      disabled={railEnds.atEnd}
                      className={cn(arrowCls, 'right-3 sm:right-5', railEnds.atEnd ? 'pointer-events-none opacity-0' : 'opacity-100 hover:scale-105')}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                    </button>
                  </div>
                  )
                })()}
                <p className="label mt-5 text-center !text-[11px] text-lc-mute">{page.people.mentorNote}</p>
              </div>
            </div>

            {/* mentor profile — lean modal (photo, craft, English about, Book),
                matching the lifecrafter bio popups */}
            <Modal open={!!profileMentor} onClose={() => setProfileMentor(null)} title={profileMentor?.name ?? ''} size="lg" theme="light">
              {profileMentor && (
                <div className="space-y-5">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                    <img src={profileMentor.photo} alt={profileMentor.name} style={{ objectPosition: profileMentor.modalFocus, scale: profileMentor.zoom ? String(profileMentor.zoom) : undefined }} className="h-20 w-20 shrink-0 rounded-2xl object-cover ring-2 ring-white/50" />
                    <div className="min-w-0">
                      <p className="label text-lc-mute">{profileMentor.craft}</p>
                      {(profileMentor.location || profileMentor.years || profileMentor.languages) && (
                        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-lc-mute">
                          {profileMentor.location && <span className="flex items-center gap-1"><Icon name="mapPin" size={12} />{profileMentor.location}</span>}
                          {profileMentor.years && <span>{profileMentor.years} experience</span>}
                          {profileMentor.languages && <span>{profileMentor.languages}</span>}
                        </div>
                      )}
                    </div>
                  </div>

                  {profileMentor.about && (
                    <div>
                      <p className="label !text-[11px] text-lc-ink">About</p>
                      <div className="mt-2 space-y-2.5 text-[14px] leading-relaxed text-lc-mute">
                        {profileMentor.about.map((para, i) => <p key={i}>{para}</p>)}
                      </div>
                    </div>
                  )}

                  <div className="pt-1">
                    <a href={crafterBook(profileMentor.name)} onClick={() => setProfileMentor(null)} className="inline-flex rounded-pill bg-lime px-6 py-3 text-[15px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100">Book a session</a>
                  </div>
                </div>
              )}
            </Modal>

            {/* View all — one tier's full roster */}
            <Modal open={!!laneModal} onClose={() => setLaneModal(null)} title={laneModal ? `${laneModal.tier} · LifeCrafters` : ''} size="xl" theme="light">
              {laneModal && (
                <div className="space-y-3">
                  <p className="label !text-[11px] text-lc-mute">
                    {tierPrice[laneModal.tier] ? (
                      <><span className="line-through decoration-1">{tierPrice[laneModal.tier].was}</span> {tierPrice[laneModal.tier].now} · until 30 Sept</>
                    ) : (
                      <><span className="line-through decoration-1">{laneModal.note}</span> · Free while we grow</>
                    )}
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {laneModal.people.map((p) => (
                      <div key={p.name} className="lc-glass-soft rounded-[18px] p-4">
                        <div className="flex items-start gap-3">
                          <PersonAvatar photo={p.photo} pos={p.imgPos} />
                          <div className="min-w-0 flex-1">
                            {p.bio ? (
                              <button type="button" onClick={() => { setLaneModal(null); setLaneBio(p) }} className="block max-w-full truncate text-left text-[15px] font-semibold text-lc-ink underline-offset-2 hover:underline">
                                {p.name}
                              </button>
                            ) : (
                              <p className="text-[15px] font-semibold text-lc-ink">{p.name}</p>
                            )}
                            <p className="text-[12px] text-lc-mute">{p.focus ?? page.topic}</p>
                          </div>
                          <a href={crafterBook(p.name)} onClick={() => setLaneModal(null)} className="bg-lime shrink-0 rounded-pill px-3 py-1.5 text-[12px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100">
                            Book
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Modal>

            {/* Single instructor bio */}
            <Modal open={!!laneBio} onClose={() => setLaneBio(null)} title={laneBio?.name ?? ''} theme="light">
              {laneBio && (
                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    {laneBio.photo && (
                      <span className="relative block h-16 w-16 shrink-0 overflow-hidden rounded-full ring-2 ring-white/50">
                        <img src={laneBio.photo} alt="" style={{ objectPosition: laneBio.modalPos, transformOrigin: laneBio.modalPos, scale: laneBio.modalZoom ? String(laneBio.modalZoom) : undefined }} className="absolute inset-0 h-full w-full object-cover" />
                      </span>
                    )}
                    {laneBio.focus && <p className="label !text-[11px] text-lc-mute">{laneBio.focus}</p>}
                  </div>
                  {laneBio.bio && <p className="text-[14px] leading-relaxed text-lc-ink/85">{laneBio.bio}</p>}
                  <a href={crafterBook(laneBio.name)} onClick={() => setLaneBio(null)} className="mt-1 inline-flex rounded-pill bg-lime px-6 py-3 text-[15px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100">Book a session</a>
                </div>
              )}
            </Modal>

            {/* price-on-request enquiry (mentors) */}
            <RequestModal open={!!requestFor} onClose={() => setRequestFor(null)} subject={requestFor ?? undefined} theme="light" />
          </section>
        )}


        {/* schools / genres, from the category board */}
        {page.types && (
          <section className="pb-20">
            <div className="container-lc px-4">
              <SectionHeading kicker={page.types.kicker} title={page.types.title} intro={page.types.intro} center />
            </div>
            <div className="relative mt-10">
            <div
              ref={typesRailRef}
              onScroll={updateTypesEnds}
              className="no-scrollbar flex snap-x gap-4 overflow-x-auto pt-2 pb-3"
              style={{
                paddingLeft: 'calc(max((100vw - 1360px) / 2, 0px) + clamp(1.25rem, 0.5rem + 3vw, 3.5rem))',
                paddingRight: 'calc(max((100vw - 1360px) / 2, 0px) + clamp(1.25rem, 0.5rem + 3vw, 3.5rem))',
                scrollPaddingLeft: 'calc(max((100vw - 1360px) / 2, 0px) + clamp(1.25rem, 0.5rem + 3vw, 3.5rem))',
                scrollPaddingRight: 'calc(max((100vw - 1360px) / 2, 0px) + clamp(1.25rem, 0.5rem + 3vw, 3.5rem))',
              }}
            >
              {page.types.items.map((t) => {
                const active = openType === t.name
                return (
                  <button
                    key={t.name}
                    onClick={() => setOpenType(t.name)}
                    aria-expanded={active}
                    style={active ? { outline: `2px solid ${page.hue.deep}`, outlineOffset: '-2px' } : undefined}
                    className={cn(
                      'lc-menu group w-[250px] shrink-0 snap-start rounded-[24px] p-5 text-left transition-all duration-300 ease-brand hover:-translate-y-1 !shadow-none',
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <span className="lc-glass-soft grid h-11 w-11 place-items-center rounded-xl transition-transform duration-500 group-hover:rotate-12" style={{ color: page.hue.deep }}>
                        <Icon name={t.icon} size={20} />
                      </span>
                      <span
                        className={cn('lc-glass-soft grid h-8 w-8 place-items-center rounded-full text-lc-mute transition-colors duration-300', active && 'bg-lime !text-night')}
                        aria-hidden="true"
                      >
                        {active ? (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m4.5 12.5 5 5 10-11" /></svg>
                        ) : (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                        )}
                      </span>
                    </div>
                    <h3 className="mt-5 font-display text-xl">{t.name}</h3>
                    <p className="mt-2 text-[14px] leading-relaxed text-lc-mute">{t.body}</p>
                    <p className={cn('label mt-4 !text-[11px] transition-colors', active ? '' : 'text-lc-mute')} style={active ? { color: page.hue.deep } : undefined}>
                      {active ? 'Reading below' : 'Tap to learn more'}
                    </p>
                  </button>
                )
              })}
            </div>
            <button
              type="button"
              aria-label="Show previous"
              onClick={() => scrollTypesRail(-1)}
              disabled={typesEnds.atStart}
              className={cn('lc-glass absolute top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 place-items-center rounded-full text-lc-ink transition duration-300 sm:grid', 'left-3 sm:left-5', typesEnds.atStart ? 'pointer-events-none opacity-0' : 'opacity-100 hover:scale-105')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
            </button>
            <button
              type="button"
              aria-label="Show more"
              onClick={() => scrollTypesRail(1)}
              disabled={typesEnds.atEnd}
              className={cn('lc-glass absolute top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 place-items-center rounded-full text-lc-ink transition duration-300 sm:grid', 'right-3 sm:right-5', typesEnds.atEnd ? 'pointer-events-none opacity-0' : 'opacity-100 hover:scale-105')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
            </button>
            </div>

            <div className="container-lc px-5">
              <div className={cn('grid transition-[grid-template-rows] duration-500 ease-brand', openType ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}>
                <div className="overflow-hidden">
                  {(() => {
                    const t = page.types.items.find((it) => it.name === openType)
                    if (!t) return null
                    return (
                      <div className="lc-glass mx-5 mt-6 overflow-hidden rounded-[28px] !shadow-none md:mx-0">
                        <div className="p-5 xl:p-7 md:p-10">
                        <div className="flex flex-col gap-6 sm:flex-row sm:items-stretch">
                          {t.image && (
                            <div className="relative aspect-[4/3] w-full shrink-0 overflow-hidden rounded-2xl sm:aspect-auto sm:w-52 md:w-60">
                              <img src={t.image} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <h3 className="font-display text-3xl">{t.name}</h3>
                            <p className="label mt-1 !text-[11px]" style={{ color: page.hue.deep }}>{t.tagline}</p>
                            <p className="label mt-6 !text-[11px] text-lc-ink">What is it?</p>
                            <p className="mt-3 max-w-2xl text-[15px] xl:text-[16px] leading-relaxed text-lc-mute">{t.what}</p>
                          </div>
                        </div>
                        <div className="mt-8 grid gap-8 md:grid-cols-3">
                          <div>
                            <p className="label !text-[11px] text-lc-ink">{page.types.labelA}</p>
                            <ul className="mt-4 space-y-2.5">
                              {t.a.map((line) => (
                                <li key={line} className="flex items-start gap-2.5 text-[14px] leading-snug text-lc-mute">
                                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: page.hue.deep }} aria-hidden="true" />
                                  {line}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <p className="label !text-[11px] text-lc-ink">{page.types.labelB}</p>
                            <ul className="mt-4 space-y-2.5">
                              {t.b.map((line) => (
                                <li key={line} className="flex items-start gap-2.5 text-[14px] leading-snug text-lc-mute">
                                  <span className="mt-0.5 shrink-0" style={{ color: page.hue.deep }} aria-hidden="true">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m4.5 12.5 5 5 10-11" /></svg>
                                  </span>
                                  {line}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="lc-glass-soft rounded-[20px] p-5">
                            <p className="label !text-[11px] text-lc-ink">Explore {t.name}</p>
                            <div className="mt-4 flex flex-col gap-2.5">
                              <a href={joinTarget} className="rounded-pill bg-lime px-4 py-2.5 text-center text-[14px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100">Join the community</a>
                              <a href="#events" className="lc-glass rounded-pill px-4 py-2.5 text-center text-[14px] font-bold text-lc-ink transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100">Explore events</a>
                              <a href={consultTarget} className="lc-glass rounded-pill px-4 py-2.5 text-center text-[14px] font-bold text-lc-ink transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100">Explore programs</a>
                            </div>
                          </div>
                        </div>
                        <p className="label mt-8 border-t border-lc-line pt-5 !text-[11px] text-lc-mute">
                          Good to know · {t.note}
                        </p>
                        </div>
                      </div>
                    )
                  })()}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* events + host (above the partner pitch) */}
        <section id="events" className="px-5 py-[8vh]">
          <div className="container-lc">
            {liveEvents && liveEvents.length > 0 ? (
              <>
                <SectionHeading
                  kicker={page.eventsKicker ?? "What's on"}
                  title={page.eventsTitle ?? 'Circles worth showing up for.'}
                  center
                />
                <div className="mx-auto mt-10 max-w-3xl space-y-3">
                  {liveEvents.map((e, i) => (
                    <EventRow key={e.id + ':' + i} day={e.day} time={e.time} kicker={e.kicker} title={e.title} blurb={e.blurb} href={eventWebappUrl(e.id)} />
                  ))}
                </div>
                <div className="mt-8 text-center">
                  <a href={eventsTarget} className="lc-glass inline-flex items-center gap-2 rounded-pill px-6 py-3 text-[15px] font-bold text-lc-ink transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100">
                    View all events
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                  </a>
                </div>
              </>
            ) : placeholderEventsFor(page.name).length > 0 ? (
              <PlaceholderEvents
                community={page.name}
                theme="light"
                heading={{ kicker: page.eventsKicker ?? "What's on", title: 'Experiences worth the trip.', intro: 'A first look at experiences coming to ' + page.name + '. Register your interest for dates and booking.' }}
              />
            ) : (
              <>
                <p className="label text-center text-lc-mute">{page.eventsKicker ?? "What's on"}</p>
                <h2 className="mx-auto mt-4 max-w-[18ch] text-center font-display text-[clamp(1.6rem,1.05rem+2.2vw,3.4rem)] font-normal leading-[1.02] tracking-[0.01em]">
                  Events are coming soon.
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-center text-lg text-lc-mute">
                  We’re still finalising the {page.name} calendar. Curated live sessions, workshops and gatherings will land here shortly. Check back soon.
                </p>
                <div className="mt-10 flex justify-center">
                  <span className="lc-glass-soft label rounded-pill px-5 py-2.5 !text-[12px] text-lc-mute">
                    Calendar in the works · coming soon
                  </span>
                </div>
              </>
            )}
          </div>
        </section>

        {/* circles: themed sub-communities, live from the webapp. No live
            circles → a "create your circle" invitation instead of stand-ins. */}
        {liveCircles && (
          <section id="circles" className="px-5 pb-20">
            <div className="container-lc">
              <SectionHeading kicker="Find your people" title="Join a circle." center />
              <p className="mx-auto mt-4 max-w-xl text-center text-lg text-lc-mute">
                Step into a smaller room within {page.name}: people practising the same craft, meeting live and talking daily. Every circle is free.
              </p>
              {liveCircles.length === 0 ? (
                <div className="lc-glass-soft mx-auto mt-12 max-w-xl rounded-[28px] p-10 text-center">
                  <p className="font-display text-2xl font-normal tracking-[0.01em] text-lc-ink">No circles here yet.</p>
                  <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-lc-mute">
                    Be the first. Start a circle and gather the people who practise what you love.
                  </p>
                  <a
                    href={circlesTarget}
                    className="mt-7 inline-flex rounded-pill bg-lime px-6 py-3 text-[15px] xl:px-7 xl:py-3.5 font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100"
                  >
                    Create your circle
                  </a>
                </div>
              ) : (
              <div
                className={cn(
                  'mt-12',
                  liveCircles.length > 4
                    ? 'no-scrollbar flex snap-x gap-6 overflow-x-auto px-1 pb-3 sm:gap-10'
                    : 'flex flex-wrap justify-center gap-x-6 gap-y-10 sm:gap-x-10',
                )}
              >
                {liveCircles.map((c, i) => (
                  <a
                    key={c.id}
                    href={circlesTarget}
                    className="group flex w-[42vw] max-w-[188px] shrink-0 snap-start flex-col items-center text-center outline-none sm:w-[188px]"
                    style={{ animationDelay: `${i * 80}ms` }}
                  >
                    <span className="relative block aspect-square w-full max-w-[188px]">
                      <span className="pointer-events-none absolute -inset-2 rounded-full border border-dashed border-lc-line opacity-40 transition-opacity duration-500 group-hover:opacity-90 motion-safe:group-hover:[animation:spin_14s_linear_infinite]" aria-hidden="true" />
                      <span className="relative block h-full w-full overflow-hidden rounded-full ring-1 ring-lc-line transition-transform duration-500 ease-brand group-hover:-translate-y-1.5 group-hover:scale-[1.03] group-hover:ring-lime/70">
                        {c.image ? (
                          <img src={c.image} alt={c.alt ?? ''} className="h-full w-full object-cover" loading="lazy" />
                        ) : (
                          <span className="grid h-full w-full place-items-center lc-glass-soft text-lc-mute">
                            <span className="font-display text-2xl" style={{ color: page.hue.deep }}>{c.name.charAt(0)}</span>
                          </span>
                        )}
                      </span>
                    </span>
                    <h3 className="mt-6 font-display text-xl">{c.name}</h3>
                    {c.blurb && <p className="mt-1.5 max-w-[24ch] text-[13px] leading-relaxed text-lc-mute">{c.blurb}</p>}
                    <span className="mt-3 inline-flex items-center gap-1.5">
                      <span className="label rounded-pill bg-lime/90 px-2 py-0.5 !text-[10px] text-night">Free</span>
                    </span>
                  </a>
                ))}
              </div>
              )}
              <p className="label mt-12 text-center !text-[11px] text-lc-mute">
                All circles are free to join · new circles open as the community grows
              </p>
            </div>
          </section>
        )}

        {/* partner pitch: the marketplace grid returns once partners are aboard */}
        {page.marketplace && (
          <PartnerCallout dark={false} accent={page.hue.deep} categories={page.marketplace.categories} />
        )}

        {/* closing */}
        <section id="join" className="px-5 pb-28">
          <div className="container-lc">
            <div className="lc-glass relative overflow-hidden rounded-card px-8 py-14 text-center">
              <span className="blob absolute left-1/2 top-0 h-64 w-64 -translate-x-1/2 opacity-50" style={{ background: '#FFD3EF' }} aria-hidden="true" />
              <div className="relative">
                <SectionHeading
                  kicker="Free to join"
                  title={`${page.name} keeps a seat for you.`}
                  intro="Join the community, sit in on one circle, and see how it feels."
                  center
                />
                <MagneticButton href="#" className="mt-8 inline-flex rounded-pill bg-lime px-6 py-3 text-[15px] xl:px-9 xl:py-4 xl:text-[17px] font-bold text-night">
                  Join {page.name}
                </MagneticButton>
              </div>
            </div>
          </div>
        </section>

        <SiteFooter dark={false} />
      </main>
    </div>
  )
}
